import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {WebServiceApiDto} from '../../../../generated-model/models';
import {resolveApiErrorMessage, WebServiceApiService} from './web-service-api-service';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../../shared/commons/toast/toast-service';

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

@Component({
  selector: 'app-web-service-api',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './web-service-api.html',
  styleUrl: './web-service-api.scss',
})
export class WebServiceApiComponent implements OnInit {
  private readonly service = inject(WebServiceApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);

  protected readonly searchQuery = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<WebServiceApiDto[]>([]);
  /** id of the row whose status is currently being toggled (disables its badge button). */
  protected readonly togglingId = signal<number | null>(null);

  private totalElements = 0;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.service.getAllWebServiceApis(this.page() - 1, this.pageSize(), this.searchQuery(), this.stateFilter()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
        this.toastService.error(resolveApiErrorMessage(err, 'โหลดข้อมูลไม่สำเร็จ'));
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.stateFilter.set(null);
    this.onSearchChange();
  }

  /** Toggles ใช้งาน/ไม่ใช้งาน of one API (old system: publish/unpublish toolbar). */
  async toggleStatus(item: WebServiceApiDto): Promise<void> {
    if (this.togglingId() != null) {
      return;
    }
    const activate = item.status !== 1;
    const confirmed = await this.confirmDialogService.confirm({
      title: activate ? 'เปิดใช้จัดการข้อมูล API' : 'ปิดใช้จัดการข้อมูล API',
      message: `ต้องการ${activate ? 'เปิดใช้งาน' : 'ปิดใช้งาน'} "${item.apiDescription || item.apiName}" ใช่หรือไม่?`,
      confirmText: 'ยืนยัน',
      cancelText: 'ยกเลิก',
      confirmButtonClass: activate ? 'btn-primary' : 'btn-danger'
    });
    if (!confirmed) {
      return;
    }
    this.togglingId.set(item.id);
    this.service.updateStatus(item.id, activate ? 1 : 0).subscribe({
      next: () => {
        this.togglingId.set(null);
        this.toastService.success(activate ? 'เปิดใช้จัดการข้อมูล API แล้ว' : 'ปิดใช้จัดการข้อมูล API แล้ว');
        this.load();
      },
      error: (err) => {
        this.togglingId.set(null);
        this.toastService.error(resolveApiErrorMessage(err, 'เปลี่ยนสถานะไม่สำเร็จ'));
      }
    });
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteWebServiceApi(id).subscribe({
      next: () => {
        this.toastService.success('ลบข้อมูลเรียบร้อยแล้ว');
        this.load();
      },
      error: (err) => this.toastService.error(resolveApiErrorMessage(err, 'ลบข้อมูลไม่สำเร็จ'))
    });
  }
}
